import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import * as luxon from "luxon";
import { FunctionComponent, useMemo } from "react";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  AreaChart,
  ReferenceLine,
} from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";
import { Button } from "../ui/button";
import { ButtonGroup } from "../ui/button-group";
import { Badge } from "../ui/badge";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";

const dateRangeWeeks = [
  { value: 1, desc: "7 days" },
  { value: 2, desc: "2 weeks" },
  { value: 4, desc: "4 weeks" },
  { value: 8, desc: "2 months" },
  { value: 12, desc: "3 months" },
  { value: 24, desc: "6 months" },
  { value: 52, desc: "12 months" },
  { value: null, desc: "All" },
];

interface ChartData {
  date: string;
  value: number;
}

interface ChartItem {
  id: string;
  name: string;
  unit: string | null;
  daily_target?: number | null;
}
interface MetricLineChartProps {
  metric: ChartItem;
  data: ChartData[];
  dateRangeWeeksValue: number | null;
  onDateRangeWeeksChanged: (days: number | null) => void;
  headerContent?: React.ReactNode;
  hideSummaryPills?: boolean;
  yAxisAllowDecimals?: boolean;
  leftButton?: {
    buttontitle: React.ReactNode;
    onLeftClicked: () => void;
    buttonIcon?: JSX.Element;
  };
  rightButton?: {
    buttontitle: React.ReactNode;
    onEditClicked: () => void;
    buttonIcon?: JSX.Element;
  };
  className?: string;
}

const MetricLineChart: FunctionComponent<MetricLineChartProps> = ({
  metric,
  data,
  dateRangeWeeksValue,
  onDateRangeWeeksChanged,
  headerContent,
  hideSummaryPills = false,
  yAxisAllowDecimals = true,
  leftButton,
  rightButton,
  className,
}) => {
  const chartConfig = useMemo(
    () =>
      ({
        value: {
          label: metric.name,
          color: "hsl(var(--primary))",
        },
      }) satisfies ChartConfig,
    [metric],
  );

  const average = useMemo(
    () =>
      data.length > 0
        ? data.reduce((sum, log) => sum + log.value, 0) / data.length
        : 0,
    [data],
  );

  const difference =
    data.length > 0
      ? getDifferencePercentage(data[0]?.value, data[data.length - 1]?.value)
      : 0;
  const sign = useMemo(() => {
    const first = data[0]?.value ?? 0;
    const last = data[data.length - 1]?.value ?? 0;
    if (first === last) return 0;
    return first < last ? 1 : -1;
  }, [data]);

  const deltaTone =
    sign === 1
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : sign === -1
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : "border-border bg-muted text-muted-foreground";
  const deltaPrefix = sign === 1 ? "+" : sign === -1 ? "-" : "";

  const interval = useMemo(
    () => Math.pow(10, Math.floor(Math.log10(average))),
    [average],
  );

  return (
    <Card className={className}>
      <CardHeader>
        <div
          className={`flex flex-row justify-between gap-4 ${
            hideSummaryPills ? "items-center" : "items-start"
          }`}
        >
          <div className={hideSummaryPills ? "min-w-0" : "mb-2 min-w-0"}>
            <CardTitle>{metric.name}</CardTitle>

            {!hideSummaryPills && (
              <CardDescription className="mt-2 flex flex-wrap items-center gap-2 py-1">
                <span className="inline-flex items-center gap-2 rounded-full border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  Avg {average.toFixed(2)} {metric.unit}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${deltaTone}`}
                >
                  {sign === 1 && <ArrowUp className="h-3.5 w-3.5" />}
                  {sign === -1 && <ArrowDown className="h-3.5 w-3.5" />}
                  {sign === 0 && <Minus className="h-3.5 w-3.5" />}
                  Change {deltaPrefix}
                  {difference}%
                </span>
              </CardDescription>
            )}
          </div>
          <ButtonGroup>
            {leftButton && (
              <Button variant="outline" onClick={leftButton.onLeftClicked}>
                {leftButton.buttonIcon} {leftButton.buttontitle}
              </Button>
            )}
            {rightButton && (
              <Button variant="outline" onClick={rightButton.onEditClicked}>
                {rightButton.buttonIcon} {rightButton.buttontitle}
              </Button>
            )}
          </ButtonGroup>
        </div>
        {headerContent && <div className="mt-4">{headerContent}</div>}
      </CardHeader>
      <ScrollArea className="mt-[-24px]">
        <div className="w-max flex-1 flex gap-2 px-6 py-2">
          {dateRangeWeeks.map((range) => (
            <Badge
              onClick={() => onDateRangeWeeksChanged(range.value)}
              key={range.desc}
              variant={
                dateRangeWeeksValue === range.value ? "default" : "secondary"
              }
              className="mr-2 mt-2 hover:cursor-pointer hover:scale-105 transition-all"
            >
              {range.desc}
            </Badge>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <CardContent className="max-lg:p-0 p-1">
        {data.length === 0 && (
          <div className="flex aspect-video items-center justify-center p-1 text-gray-500 lg:p-4">
            No data available
          </div>
        )}
        {data.length > 0 && (
          <ChartContainer
            key={metric.id}
            config={chartConfig}
            className="p-1 lg:p-4"
          >
            <AreaChart accessibilityLayer data={data} reverseStackOrder>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  luxon.DateTime.fromISO(value).toFormat("LLL d")
                }
                interval={"preserveStartEnd"}
              />
              <YAxis
                dataKey="value"
                allowDecimals={yAxisAllowDecimals}
                tickFormatter={(value) =>
                  metric.unit ? `${value} ${metric.unit}` : value
                }
                tickLine={false}
                axisLine={false}
                tickCount={10}
                domain={([dataMin, dataMax]) => {
                  const min = Math.max(dataMin, 0);
                  if (metric.daily_target && metric.daily_target > dataMax) {
                    return [min, metric.daily_target + interval];
                  }
                  return [min, dataMax];
                }}
              />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              {metric.daily_target && (
                <ReferenceLine
                  y={metric.daily_target}
                  stroke="hsl(var(--primary))"
                  opacity={0.4}
                  label={`Target: ${metric.daily_target}${
                    metric.unit ? ` ${metric.unit}` : ""
                  }`}
                />
              )}
              <defs>
                <linearGradient id="fillValue" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="var(--color-value)"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--color-value)"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <Area
                type="bumpX"
                dataKey="value"
                strokeWidth={2}
                fillOpacity={0.4}
                fill="url(#fillValue)"
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export function getDifferencePercentage(
  start: number | null,
  end: number | null,
  fixed: number = 2,
) {
  if (start === null || end === null) {
    return 0;
  }
  const dif = (end - start) / Math.abs(start);
  return Math.abs(dif * 100).toFixed(fixed);
}

export { MetricLineChart };
